import {
  Alert,
  Button,
  Drawer,
  Popconfirm,
  Skeleton,
  Table,
  Tag,
  Typography,
  notification
} from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { readSettingsError } from "../settings-error-envelope.ts"
import { monthLabel } from "./budget-months.ts"
import {
  cancelScheduledBudget,
  getLeadSourceBudgetHistory
} from "./lead-source-budgets.services.ts"
import type {
  LeadSourceBudgetHistoryEntry,
  LeadSourceBudgetRow,
  LeadSourceBudgetStatus
} from "./lead-source-budgets.types.ts"

const STATUS_COLOR: Record<LeadSourceBudgetStatus, string> = {
  active: "green",
  scheduled: "blue",
  past: "default",
  superseded: "default",
  cancelled: "red"
}

export interface LeadSourceBudgetHistoryDrawerProps {
  budget: LeadSourceBudgetRow
  onClose: () => void
  /** A cancelled scheduled change moves the list's Scheduled column. */
  onChanged: () => void | Promise<void>
}

/** Every budget version a lead source has had, newest month first. Nothing can
 *  be deleted; a change that has not started yet can be cancelled. */
export const LeadSourceBudgetHistoryDrawer = ({
  budget,
  onClose,
  onChanged
}: LeadSourceBudgetHistoryDrawerProps) => {
  const { t, i18n } = useTranslation()
  const [entries, setEntries] = useState<LeadSourceBudgetHistoryEntry[] | null>(
    null
  )
  const [failed, setFailed] = useState(false)
  const [version, setVersion] = useState(budget.version)

  const load = useCallback(async () => {
    const response = await getLeadSourceBudgetHistory(budget.source)
    if (response.ok) {
      setEntries(response.data?.results ?? [])
      setFailed(false)
    } else {
      setFailed(true)
    }
  }, [budget.source])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => setVersion(budget.version), [budget.version])

  const cancel = async (entry: LeadSourceBudgetHistoryEntry) => {
    const response = await cancelScheduledBudget(entry.id, version)
    if (!response.ok) {
      notification.error({
        message:
          response.status === 409
            ? t("leadSourceBudgets.errors.stale")
            : readSettingsError(response.data) ||
              t("leadSourceBudgets.errors.cancel")
      })
    }
    await load()
    await onChanged()
  }

  return (
    <Drawer
      open
      width={760}
      title={t("leadSourceBudgets.history.title", { source: budget.label })}
      onClose={onClose}
    >
      {failed ? (
        <Alert
          type={"error"}
          message={t("leadSourceBudgets.errors.history")}
          action={
            <Button size={"small"} onClick={() => void load()}>
              {t("leadSourceBudgets.actions.retry")}
            </Button>
          }
        />
      ) : entries === null ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <Table<LeadSourceBudgetHistoryEntry>
          size={"small"}
          rowKey={"id"}
          pagination={false}
          dataSource={entries}
          locale={{ emptyText: t("leadSourceBudgets.history.empty") }}
          scroll={{ x: "max-content" }}
          columns={[
            {
              title: t("leadSourceBudgets.history.month"),
              dataIndex: "effective_month",
              render: (value: string) => monthLabel(value, i18n.language)
            },
            {
              title: t("leadSourceBudgets.history.amount"),
              dataIndex: "amount",
              align: "right",
              render: (value: string) => budgetFormat(value)
            },
            {
              title: t("leadSourceBudgets.history.appliedAs"),
              dataIndex: "apply_from",
              render: (value: string) =>
                t(`leadSourceBudgets.applyFrom.${value}`)
            },
            {
              title: t("leadSourceBudgets.history.status"),
              dataIndex: "status",
              render: (value: LeadSourceBudgetStatus) => (
                <Tag color={STATUS_COLOR[value]}>
                  {t(`leadSourceBudgets.status.${value}`)}
                </Tag>
              )
            },
            {
              title: t("leadSourceBudgets.history.reason"),
              dataIndex: "reason",
              render: (value: string) => value || "—"
            },
            {
              title: t("leadSourceBudgets.history.changedBy"),
              dataIndex: "created_by",
              render: (value: string | null, entry) => (
                <Typography.Text>
                  {value ?? "—"}
                  <Typography.Text
                    type={"secondary"}
                    style={{ display: "block", fontSize: 12 }}
                  >
                    {new Date(entry.created).toLocaleString(i18n.language)}
                  </Typography.Text>
                </Typography.Text>
              )
            },
            {
              title: "",
              render: (_: unknown, entry) =>
                entry.status === "scheduled" ? (
                  <Popconfirm
                    title={t("leadSourceBudgets.history.cancelConfirm", {
                      amount: budgetFormat(entry.amount),
                      month: monthLabel(entry.effective_month, i18n.language)
                    })}
                    okText={t("leadSourceBudgets.history.cancelScheduled")}
                    onConfirm={() => cancel(entry)}
                  >
                    <Button size={"small"} danger type={"link"}>
                      {t("leadSourceBudgets.history.cancelScheduled")}
                    </Button>
                  </Popconfirm>
                ) : null
            }
          ]}
        />
      )}
    </Drawer>
  )
}
